import { UseInfiniteQueryResult, useInfiniteQuery } from 'react-query';
import pDebounce from 'p-debounce';

import { useAuth } from 'src/hooks';
import { getAllLogs } from 'src/utils/urls'; // ✅ updated URL reference
import { pageLength } from 'src/utils/appConstants';
import { setListRequestData } from '../utils/listReqData';
import { activityStore } from '../store/activityStore';

import { ActivityLogFilterType } from '../types/filterOptionsType';
import { PersonAlertLogType } from '../types/activityLogType';
import { allLogListReqData } from '../utils/allLogListReqData';

export type TVariables = {
  requestData: URLSearchParams;
  token: string;
  tokenType: string;
  pageParam: number;
};

export type TError = { message: string };

export type TPage = {
  data: Array<PersonAlertLogType>;
  draw: number;
  error: null;
  isAutoRefreshEnabled: boolean;
  pageNumber: number;
  recordsFiltered: number;
  recordsTotal: number;
};

async function fetchAllLogs({
  requestData,
  token,
  tokenType,
  pageParam,
}: TVariables): Promise<TPage> {
  requestData.append('start', `${pageParam}`);
  activityStore.getState().setPageParam(pageParam);
  const response = await fetch(getAllLogs, {
    method: 'POST',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: requestData,
  });

  if (!response.ok) {
    throw new Error(`Fetch failed with status ${response.status}`);
  }

  return response.json();
}

const debouncedFetch = pDebounce(fetchAllLogs, 500);

export function useGetAllLogs(
  filterOptions: ActivityLogFilterType,
  selectedToggle: string,
  isActivity: boolean,
  isActivityEmpty?: boolean,
): UseInfiniteQueryResult<TPage, unknown> {
  const { token, tokenType } = useAuth();

  return useInfiniteQuery(
    ['get_all_logs', filterOptions, selectedToggle],
    ({ pageParam = 0 }) => {
      const requestData = allLogListReqData(filterOptions, 'list');

      console.log('All Logs Request Data:', { requestData, isActivity });
      return debouncedFetch({ token, tokenType, requestData, pageParam });
    },
    {
      getNextPageParam: (lastPage: TPage, allPages: Array<TPage>) => {
        if (lastPage?.data?.length === pageLength)
          return allPages.length * pageLength;
      },
      getPreviousPageParam: (firstPage: TPage, allPages: Array<TPage>) => {
        if (firstPage?.data?.length === pageLength)
          return (allPages.length - 1) * pageLength;
      },
      enabled: selectedToggle === 'all' && isActivity && !isActivityEmpty,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}
